/**
 * Hook for managing timezone detection notifications
 * Provides state management and notifications for timezone auto-detection
 */

import { useState, useCallback, useEffect } from 'react'
import { useAuth } from '@/lib/auth'
import { shouldAutoDetectTimezone, autoDetectUserTimezone, TimezoneAutoDetectionResult } from '@/lib/timezone-auto-detect'
import { useToast } from '@/components/ui/use-toast'
import { useNavigate } from 'react-router-dom'

interface TimezoneDetectionState {
  isDetecting: boolean
  hasDetected: boolean
  detectedTimezone: string | null
  showNotification: boolean
  error: string | null
}

interface UseTimezoneDetectionNotificationOptions {
  autoTrigger?: boolean
  showToast?: boolean
  redirectToSettings?: boolean
}

export function useTimezoneDetectionNotification(
  options: UseTimezoneDetectionNotificationOptions = {}
) {
  const { 
    autoTrigger = true, 
    showToast = true,
    redirectToSettings = false
  } = options
  
  const { user } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  
  const [state, setState] = useState<TimezoneDetectionState>({
    isDetecting: false,
    hasDetected: false,
    detectedTimezone: null,
    showNotification: false,
    error: null
  })

  // Auto-trigger detection when user is available
  useEffect(() => {
    if (!autoTrigger || !user || state.hasDetected || state.isDetecting) {
      return
    }

    detectTimezone()
  }, [user, autoTrigger])

  const detectTimezone = useCallback(async () => {
    if (!user) return

    setState(prev => ({
      ...prev,
      isDetecting: true,
      error: null
    }))

    try {
      const needsDetection = await shouldAutoDetectTimezone(user)
      if (!needsDetection) {
        setState(prev => ({
          ...prev,
          isDetecting: false,
          hasDetected: true
        }))
        return
      }

      const result = await autoDetectUserTimezone(user, {
        updateProfile: true,
        silentMode: false
      })

      setState(prev => ({
        ...prev,
        isDetecting: false,
        hasDetected: true,
        detectedTimezone: result.timezone,
        showNotification: result.detected && !!result.timezone,
        error: result.error || null
      }))

      // Show toast notification if enabled
      if (showToast && result.detected && result.timezone) {
        showTimezoneDetectedToast(result)
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      setState(prev => ({
        ...prev,
        isDetecting: false,
        error: errorMessage
      }))

      if (showToast) {
        toast({
          title: "Timezone Detection Failed",
          description: "We couldn't automatically detect your timezone. Please set it manually in your profile settings.",
          variant: "destructive",
        })
      }
    }
  }, [user, showToast, toast])

  const showTimezoneDetectedToast = useCallback((result: TimezoneAutoDetectionResult) => {
    if (!result.timezone) return

    const timezoneDisplay = result.timezone
      .split('/')
      .pop()
      ?.replace(/_/g, ' ') || result.timezone

    toast({
      title: "Timezone Auto-Detected",
      description: `We've automatically set your timezone to ${timezoneDisplay}. You can change this in your profile settings.`,
    })
  }, [toast, navigate, redirectToSettings])

  const dismissNotification = useCallback(() => {
    setState(prev => ({
      ...prev,
      showNotification: false
    }))
  }, [])

  const openSettings = useCallback(() => {
    dismissNotification()
    navigate('/settings')
  }, [dismissNotification, navigate])

  const retryDetection = useCallback(() => {
    setState(prev => ({
      ...prev,
      hasDetected: false,
      error: null,
      showNotification: false
    }))
    detectTimezone()
  }, [detectTimezone])

  return {
    ...state,
    detectTimezone,
    dismissNotification,
    openSettings,
    retryDetection
  }
}

export default useTimezoneDetectionNotification